import assert from 'assert';
import path from 'path';
import superagent from 'superagent';
import typeCheck from 'common/util/typeCheck';
import parseS3Url from 'common/util/parseS3Url';
import log from 'client/lib/log';

const debug = log.debug('sunroom:fileUploader');

const UUID_V4_REGEX = /-[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/;
const UNIX_DATE_REGEX = /-\d{13}/;
const UID_REGEX = /uid[0-9]+-/g;

/**
 * Removes Sunroom specific timestamps and unique IDs from an S3 URL.
 *
 * Timestamped S3 URL: https://s3.amazonaws.com/{BUCKET_NAME}/renter-application-pdfs/christopher-jenkins-tar-1557274594278.pdf
 * UUID S3 URL:        https://s3.amazonaws.com/{BUCKET_NAME}/renter-application-uploads/christopher-jenkins-id-11befc31598e4252a74083161efb06d9.jpg
 */
export function sanitizeSrS3Url(srS3Url) {
  return srS3Url.replace(UUID_V4_REGEX, '').replace(UNIX_DATE_REGEX, '').replace(UID_REGEX, '');
}

/**
 * Creates a file object with various useful fields for storage or uploading via 'getFileBlobFromObjectUrl'.
 *
 * @param {File} file.
 *
 * @return {Object}
 */
export function createFileObjToBeUploaded(file) {
  assert(process.browser, 'createFileObjToBeUploaded is not available on server-side.');
  const fileInfoObject = {
    fileBlobUrl: global.URL.createObjectURL(file),
    uploadTime: Date.now(),
    preview: file.preview,
    name: file.name,
    size: file.size,
    type: file.type,
  };
  debug('Created file object to be uploaded', fileInfoObject);
  return fileInfoObject;
}

/**
 * Gets a Blob instance out of an objectURL string.
 *
 * @param   {string}  objectUrl  A string created with `URL.createObjectUrl()`.
 * @return  {Blob}               The file's blob.
 */
export function getFileBlobFromObjectUrl(objectUrl) {
  return new Promise((resolve, reject) => {
    typeCheck('objectUrl::NonEmptyString', objectUrl);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', objectUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = function onObjectUrlRead() {
      debug(`Loaded file blob for ${objectUrl}`, { status: this.status });
      if (this.status === 200) {
        resolve(this.response);
      } else {
        reject(new Error(`Failed to get file blob from object URL: ${objectUrl}`));
      }
    };
    xhr.send();
  });
}

export async function uploadFileToS3(fileObjToBeUploaded, params) {
  typeCheck('fileObjToBeUploaded::SrFileObject', fileObjToBeUploaded);
  typeCheck('params::NonEmptyObject', params);
  typeCheck('filePath::NonEmptyString', params.filePath);
  typeCheck('isPrivate::Maybe Boolean', params.isPrivate);
  typeCheck('uploadAs::Maybe PositiveNumber', params.uploadAs);

  const { name, fileBlobUrl } = fileObjToBeUploaded;

  const { filePath, isPrivate, uploadAs } = params;

  const fileBlob = await getFileBlobFromObjectUrl(fileBlobUrl);

  const originalFileExtension = path.extname(name) ? path.extname(name).replace('.', '') : '';

  const formData = new FormData();
  formData.append('file', fileBlob);
  formData.append('filePath', filePath);
  formData.append('fileExtension', originalFileExtension);
  formData.append('isPrivate', isPrivate);
  if (uploadAs) {
    formData.append('uploadAs', uploadAs);
  }

  const uploadResponse = await superagent.post('/upload-file', formData);
  const uploadResponseBody = uploadResponse.body;

  debug('Uploaded file', {
    fileObjToBeUploaded,
    params,
    uploadResponseBody,
  });

  if (!uploadResponseBody.success) {
    throw uploadResponseBody.error;
  }

  return uploadResponseBody.data;
}

/**
 * Uploads a file straight to a service-owned S3 bucket using a presigned PUT URL
 * minted by the caller (via `getUploadUrl`), instead of the generic `/upload-file`
 * endpoint which targets web's own `AWS_BUCKET`.
 *
 * Use this when the object must live in a DIFFERENT service's bucket — e.g. the
 * admin "upload policy on renter's behalf" flow, whose proof must land in core's
 * `USER_UPLOAD_BUCKET` so core's download endpoint can presign and serve it. The
 * returned `s3FileUrl` matches `uploadFileToS3`'s shape so it drops into
 * `SrFileUploader` as a custom `uploadFn`.
 *
 * @param {Object}   fileObjToBeUploaded  Result of `createFileObjToBeUploaded`.
 * @param {Object}   params
 * @param {Function} params.getUploadUrl  async ({ fileName, mediaType, size }) =>
 *                                        { url, objectUrl } — `url` is the presigned
 *                                        PUT, `objectUrl` is the stable object URL.
 * @return {Promise<{ s3FileUrl: string }>}
 */
export async function uploadFileToUserBucket(fileObjToBeUploaded, params) {
  typeCheck('fileObjToBeUploaded::SrFileObject', fileObjToBeUploaded);
  typeCheck('params::NonEmptyObject', params);
  assert(typeof params.getUploadUrl === 'function', 'getUploadUrl must be a function');

  const { name, type, size, fileBlobUrl } = fileObjToBeUploaded;
  const mediaType = type || 'application/octet-stream';

  const uploadUrlResponse = await params.getUploadUrl({ fileName: name, mediaType, size });

  if (!uploadUrlResponse || !uploadUrlResponse.url || !uploadUrlResponse.objectUrl) {
    throw new Error('Failed to obtain a presigned upload URL');
  }

  const { url, objectUrl } = uploadUrlResponse;
  const fileBlob = await getFileBlobFromObjectUrl(fileBlobUrl);

  // Direct PUT to S3. The presigned URL signs only `host` (ACL is hoisted to a
  // query param), so Content-Type is unsigned and safe to set for inline viewing.
  await putBlobToSignedUrl(url, fileBlob, mediaType);

  debug('Uploaded file to user bucket', { objectUrl });

  return { s3FileUrl: objectUrl };
}

/**
 * PUTs a Blob to a presigned S3 URL. Uses XMLHttpRequest (matching
 * `getFileBlobFromObjectUrl`) rather than superagent so the binary body is sent
 * verbatim with no serialization.
 */
function putBlobToSignedUrl(signedUrl, fileBlob, contentType) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.onload = function onUploadComplete() {
      if (this.status >= 200 && this.status < 300) {
        resolve();
      } else {
        reject(new Error(`Failed to upload file to S3 (status ${this.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while uploading file to S3'));
    xhr.send(fileBlob);
  });
}

export function makeProxiedS3FilePath(s3FileUrl, appUrl) {
  const parsedS3FileUrl = parseS3Url(s3FileUrl);

  if (!parsedS3FileUrl) {
    return s3FileUrl;
  }
  return `${appUrl || ''}/aws-s3/${parsedS3FileUrl.bucket}/${parsedS3FileUrl.key}`;
}

export function getImageDimension(fileUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.src = fileUrl;
  });
}

export async function getFileValidationError(fileObjectToBeUploaded, params) {
  const mimeType = fileObjectToBeUploaded.type;
  const { minWidth = 0, minHeight = 0 } = params;

  // Image size validation check
  if ((minWidth || minHeight) && !['application/pdf', 'image/heic'].includes(mimeType)) {
    if (!mimeType.includes('image')) {
      return 'Invalid image file';
    }

    const imageDimension = await getImageDimension(fileObjectToBeUploaded.fileBlobUrl);
    if (imageDimension.width < minWidth || imageDimension.height < minHeight) {
      if (minWidth && minHeight) {
        return (
          'This image is too small, ' +
          `please upload an image that’s at least ${minWidth}px x ${minHeight}px in dimension.`
        );
      }
      if (minWidth) {
        return (
          'This image is too small, ' +
          `please upload an image that’s at least ${minWidth}px width.`
        );
      }
      return (
        'This image is too small, ' +
        `please upload an image that’s at least ${minHeight}px height.`
      );
    }
  }

  // Add more validation checks

  // No validation error, return null
  return null;
}

export function isPDFFile(filePath) {
  const fileExtension = path
    .extname(filePath || '')
    .replace('.', '')
    .toLowerCase();
  return fileExtension === 'pdf';
}
